import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Users, Plus, Trash2, UserCheck, UserX } from 'lucide-react';

export default function SubusersPage() {
  const [subusers, setSubusers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const loadSubusers = useCallback(async () => {
    try {
      const data = await api.getSubusers();
      setSubusers(data.subusers || []);
    } catch (err) {
      console.error('Failed to load subusers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSubusers(); }, [loadSubusers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password) {
      setError('Email and password are required');
      return;
    }
    setCreating(true);
    try {
      await api.createSubuser(form);
      setShowCreate(false);
      setForm({ email: '', password: '', name: '' });
      await loadSubusers();
    } catch (err) {
      setError(err.message || 'Failed to create subuser');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this subuser?')) return;
    try {
      await api.deleteSubuser(userId);
      await loadSubusers();
    } catch (err) {
      console.error('Failed to delete subuser:', err);
    }
  };

  const handleToggleActive = async (userId, currentActive) => {
    try {
      await api.updateSubuser(userId, { is_active: !currentActive });
      await loadSubusers();
    } catch (err) {
      console.error('Failed to toggle subuser:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="subusers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Team Members
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage subusers who share your jobs and credits
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-subuser-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Member
        </Button>
      </div>

      {subusers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No team members yet. Add subusers to share access to your jobs.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {subusers.map(user => (
            <Card key={user.id} data-testid={`subuser-${user.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                      user.is_active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                    }`}>
                      {(user.name || user.email).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{user.name || user.email}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <Badge variant={user.is_active ? 'default' : 'secondary'} className="text-xs ml-2">
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(user.id, user.is_active)}
                      data-testid={`toggle-subuser-${user.id}`}
                    >
                      {user.is_active
                        ? <UserX className="w-4 h-4 text-muted-foreground" />
                        : <UserCheck className="w-4 h-4 text-primary" />
                      }
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(user.id)}
                      data-testid={`delete-subuser-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label htmlFor="sub-name">Name</Label>
              <Input
                id="sub-name"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
                data-testid="subuser-name-input"
              />
            </div>
            <div>
              <Label htmlFor="sub-email">Email</Label>
              <Input
                id="sub-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="email@example.com"
                required
                data-testid="subuser-email-input"
              />
            </div>
            <div>
              <Label htmlFor="sub-pw">Password</Label>
              <Input
                id="sub-pw"
                type="password"
                value={form.password}
                onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Min. 6 characters"
                required
                data-testid="subuser-password-input"
              />
            </div>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="submit-subuser-btn">
                {creating ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
